'use client'

import Link from 'next/link'
import { useCallback, useMemo, useState } from 'react'
import { Loader2, PanelTopCloseIcon, PanelTopOpenIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { getUser, updateUser } from '@/app/actions/user'
import { Button } from '@/components/ui/button'
import { signUpHref } from '@/constants/authRoutes'
import { DashboardProvider } from '@/providers/Dashboard'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

import { GuidedIntro } from './components/GuidedIntro'
import Member from './components/Member'
import { MyBills } from './components/MyBills'
// import { MyEvents } from './components/MyEvents'
// import { UpcomingEvents } from './components/MyEvents/components/UpcomingEvents'
// import { SavedArticles } from './components/SavedArticles'
import { WinTracker } from './components/WinTracker'
import { XTimeline } from './components/XTimeline'

import type { Bill, Topic, User } from '@/payload-types'

export const Dashboard = ({
  payloadUser,
  billTopics = [],
  showMembershipCheckoutSuccess = false,
}: {
  payloadUser: User | null
  billTopics?: Topic[]
  showMembershipCheckoutSuccess?: boolean
}) => {
  const [userData, setUserData] = useState<User | null>(payloadUser)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isMobile } = useLenisContext()

  const userTopics = useMemo(
    () => (userData?.topics?.topics ?? []).map((topic) => topic as Topic),
    [userData?.topics?.topics],
  )

  // Sidebar animation config
  const sidebarMotion = useMemo(() => {
    if (isMobile) {
      return {
        initial: sidebarOpen ? false : { opacity: 0, x: '-100%' },
        animate: sidebarOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: '-100%' },
        exit: sidebarOpen ? { opacity: 0, x: '-100%' } : { opacity: 1, x: 0 },
      }
    }

    return {
      initial: false,
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0 },
    }
  }, [sidebarOpen, isMobile])

  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen)

  const refreshUser = useCallback(async (updatedUser?: User) => {
    if (updatedUser !== null && updatedUser !== undefined) {
      setUserData(updatedUser)
      return
    }

    const data = await getUser()

    if (data.status === 'error') {
      setUserData(null)
      return
    }

    setUserData(data.data)
  }, [])

  // Tied to userData: read from user, persist on change
  const showXTimeline = userData?.dashboard?.showXTimeline ?? true
  const showMyBills = userData?.dashboard?.showMyBills ?? true
  const showMyEvents = userData?.dashboard?.showMyEvents ?? true
  const showSavedArticles = userData?.dashboard?.showSavedArticles ?? true
  const showWinTracker = userData?.dashboard?.showWinTracker ?? true
  const updateDashboard = useCallback(async (partial: Partial<NonNullable<User['dashboard']>>) => {
    const data = await getUser()

    if (data.status === 'error') {
      setUserData(null)
      return
    }

    const user = data.data

    const res = await updateUser({
      ...user,
      dashboard: { ...user.dashboard, ...partial },
    })

    if (res.status === 'error') {
      setUserData(null)
      return
    }

    setUserData(res.data)
  }, [])

  const setShowXTimeline = useCallback(
    (show: boolean) => updateDashboard({ showXTimeline: show }),
    [updateDashboard],
  )
  const setShowMyBills = useCallback(
    (show: boolean) => updateDashboard({ showMyBills: show }),
    [updateDashboard],
  )
  const setShowMyEvents = useCallback(
    (show: boolean) => updateDashboard({ showMyEvents: show }),
    [updateDashboard],
  )
  const setShowSavedArticles = useCallback(
    (show: boolean) => updateDashboard({ showSavedArticles: show }),
    [updateDashboard],
  )
  const setShowWinTracker = useCallback(
    (show: boolean) => updateDashboard({ showWinTracker: show }),
    [updateDashboard],
  )

  // Auth settled and no user – show a login prompt for the personal sections,
  // but still render the public Upcoming Events module for anonymous visitors.
  if (userData === null) {
    return (
      <div className="wrapper flex flex-col gap-8 py-10 max-sm:px-3">
        <div className="flex min-h-[30vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-muted-foreground">Please log in to view your Dashboard.</p>
          <div className="flex flex-row gap-2">
            <Button asChild className="shrink-0 whitespace-nowrap">
              <Link href={'/sign-in'}>Log in</Link>
            </Button>
            <Button asChild className="shrink-0 whitespace-nowrap">
              <Link href={signUpHref()}>Sign up</Link>
            </Button>
          </div>
        </div>
        {/* <div
          className="mx-auto flex w-full max-w-3xl flex-col gap-4 rounded-2xl border border-border bg-background p-4 lg:p-6"
          data-lenis-prevent
        >
          <h2 className="flex items-center gap-2 font-body text-xl font-medium text-foreground">
            <CalendarDays />
            Upcoming Events
          </h2>
          <UpcomingEvents user={null} />
        </div> */}
      </div>
    )
  }

  // User exists but Payload user not loaded yet
  if (userData === undefined) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <Loader2 className="size-8 animate-spin text-muted-foreground" aria-label="Loading..." />
        <p className="text-sm text-muted-foreground">Loading Profile</p>
      </div>
    )
  }

  const trackedBills = (userData.Bills?.bills ?? []).filter(
    (b): b is Bill => typeof b === 'object' && b !== null && 'slug' in b,
  )

  // const savedArticles = (userData.Articles?.articles ?? []).filter(
  //   (b): b is Article => typeof b === 'object' && b !== null && 'slug' in b,
  // )

  return userData !== null && !userData?.introCompleted ? (
    <GuidedIntro
      user={userData}
      onUserUpdated={refreshUser}
      showMembershipCheckoutSuccess={showMembershipCheckoutSuccess}
    />
  ) : (
    <DashboardProvider
      value={{
        userData,
        refreshUser,
        sidebarOpen,
        setSidebarOpen,
        showXTimeline,
        setShowXTimeline,
        showMyBills,
        setShowMyBills,
        showMyEvents,
        setShowMyEvents,
        showSavedArticles,
        setShowSavedArticles,
        showWinTracker,
        setShowWinTracker,
        updateDashboard,
      }}
    >
      <div className="relative wrapper py-10 max-sm:px-3">
        <div className="relative z-30 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(324px,340px)_minmax(0,1fr)] lg:gap-5 xl:gap-8">
          {/* Mobile sidebar toggle button */}
          <Button
            variant="icon"
            className={cn(
              'fixed top-20 -left-1 z-50 size-11 cursor-pointer rounded-l-none bg-accent shadow-md shadow-primary/10 lg:hidden',
            )}
            onClick={handleSidebarToggle}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={sidebarOpen ? 'close' : 'open'}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -4 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
              >
                {sidebarOpen ? (
                  <PanelTopCloseIcon className="size-6 -rotate-90" />
                ) : (
                  <PanelTopOpenIcon className="size-6 -rotate-90" />
                )}
              </motion.div>
            </AnimatePresence>
          </Button>

          {/* Sidebar blur */}
          <motion.div
            className="sidebar-blur fixed top-0 left-0 z-20 size-full bg-black/5 backdrop-blur-xs lg:hidden"
            initial={false}
            animate={
              sidebarOpen
                ? { opacity: 1, visibility: 'visible' }
                : { opacity: 0, visibility: 'hidden' }
            }
            transition={{
              duration: 0.2,
              delay: sidebarOpen ? 0 : 0.1,
              ease: 'easeOut',
            }}
            onClick={() => setSidebarOpen(false)}
          />
          {/* Sidebar */}
          <motion.div
            initial={sidebarMotion.initial}
            animate={sidebarMotion.animate}
            exit={sidebarMotion.exit}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              'scrollbar-hidden relative z-30 flex flex-col items-start gap-4 overflow-y-auto max-lg:max-h-[calc(100vh-84px-4rem)]',
              'self-start rounded-2xl border border-border bg-background px-4 pt-6 pb-0 max-lg:fixed max-lg:inset-x-4 max-lg:top-21 max-lg:z-20 max-lg:w-fit max-lg:border max-lg:border-border max-lg:bg-background max-lg:p-4 max-lg:shadow-md max-lg:shadow-primary/10 max-sm:w-[calc(100%-32px)] sm:max-lg:min-w-75 md:px-3',
              isMobile && !sidebarOpen && 'pointer-events-none',
            )}
            data-lenis-prevent
          >
            {userData && <Member user={userData} onUserUpdated={refreshUser} />}
          </motion.div>

          {userData && (
            <div className="flex min-w-0 flex-col gap-4 lg:gap-5 xl:gap-8">
              <h1 className="font-family-display text-2xl text-primary md:text-4xl lg:hidden">
                Dashboard
              </h1>
              <div
                className={cn(
                  'flex min-w-0 flex-wrap gap-4',
                  '*:w-full *:min-w-0',
                  'lg:*:w-[calc(50%-0.5rem)]!',
                )}
              >
                {showMyBills && <MyBills bills={trackedBills} allBillTopics={billTopics} />}
                {showWinTracker && (
                  <WinTracker
                    userTopics={userTopics}
                    userState={userData?.location?.location?.state ?? undefined}
                  />
                )}
                {/* {showMyEvents && <MyEvents user={userData} />} */}
                {/* {showSavedArticles && <SavedArticles articles={savedArticles} />} */}
              </div>
              {showXTimeline && <XTimeline />}
            </div>
          )}
        </div>
      </div>
    </DashboardProvider>
  )
}
